import React, { useState, useEffect } from 'react';
import { graphql, Link } from 'gatsby';
import { useInView } from 'react-intersection-observer';
import PropTypes from 'prop-types';

import {
  SEO,
  Layout,
  ProductFilters,
  Tile,
  Spinner,
  CollectionGrid,
  JustLanded,
  JustDropped,
  NewsletterSignup,
  GoogleMap,
} from '../components';

function CollectionPageTemplate({ data: { shopifyCollection } }) {
  const { products } = shopifyCollection;

  // set products
  const [prods, setProds] = useState([]);

  //  set size filter
  const [sizeFilter, setSizeFilter] = useState('All');

  // set items to show
  const INCREMENTOR = 16;
  const [itemsToShow, setItemsToShow] = useState(INCREMENTOR);

  useEffect(() => {
    // Start off with all products in the collection
    let tempProds = JSON.parse(JSON.stringify(products));

    // Then handle the size filter
    if (sizeFilter !== 'All') {
      tempProds = tempProds.filter(
        (prod) =>
          prod.options.find((o) => o.values.includes(sizeFilter)) !== undefined
      );
    }

    setProds(tempProds);
  }, [products, itemsToShow, sizeFilter]);

  // Set up infinite scroll
  const [ref, inView] = useInView({
    threshold: 0,
    triggerOnce: false,
  });

  useEffect(() => {
    if (inView)
      setItemsToShow((prev) =>
        prev + INCREMENTOR < prods.length ? prev + INCREMENTOR : prods.length
      );
  }, [inView, prods.length]);

  return (
    <Layout>
      <SEO
        title={shopifyCollection.title}
        description={shopifyCollection.description}
      />
      <div className="flex items-center px-4 py-2 tracking-wider text-white uppercase sm:px-6 lg:px-8 bg-light-pink">
        <div className="w-1/3 text-sm">
          <Link
            to="/"
            className="hover:underline focus:shadow-none focus:underline"
          >
            Home
          </Link>
          <span aria-hidden className="mx-2">
            /
          </span>
          <Link
            to={`/collections/${shopifyCollection.handle}/`}
            className="hover:underline focus:shadow-none focus:underline"
          >
            {shopifyCollection.title}
          </Link>
        </div>
        <div className="w-1/3 text-center">
          <h1 className="font-medium">{shopifyCollection.title}</h1>
        </div>
      </div>
      <div className="grid gap-6">
        <div className="w-full max-w-xs px-4 mx-auto sm:max-w-2xl lg:max-w-7xl sm:px-6 lg:px-8">
          <ProductFilters
            products={products}
            setSizeFilter={setSizeFilter}
            setItemsToShow={setItemsToShow}
          />
          <div className="grid gap-4 mt-4 sm:grid-cols-2 lg:grid-cols-4">
            {prods
              ? prods
                  .slice(0, itemsToShow)
                  .map((product) => (
                    <Tile
                      key={product.handle}
                      slug={`/products/${product.handle}/`}
                      title={product.title}
                      price={Number(product.priceRange.minVariantPrice.amount)}
                      image={product.images[0]}
                    />
                  ))
              : Array(itemsToShow)
                  .fill('')
                  .map((_, i) => (
                    <div key={i} className="relative h-0 aspect-ratio-square">
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Spinner />
                      </div>
                    </div>
                  ))}
            {prods && prods.length === 0 && (
              <p className="text-center md:col-span-2 lg:col-span-4">
                Sorry, there are no products in this collection.
              </p>
            )}
          </div>
        </div>
        {itemsToShow < prods.length && (
          <div
            ref={ref}
            className="relative flex items-center justify-center h-24 px-4 sm:px-6 lg:px-8"
          >
            <Spinner />
          </div>
        )}
        <CollectionGrid />
        <JustLanded />
        <JustDropped />
      </div>
      <NewsletterSignup />
      <GoogleMap />
    </Layout>
  );
}

CollectionPageTemplate.propTypes = {
  data: PropTypes.object.isRequired,
};

export const query = graphql`
  query($handle: String!) {
    shopifyCollection(handle: { eq: $handle }) {
      id
      title
      handle
      description
      shopifyId
      products {
        tags
        title
        handle
        productType
        vendor
        availableForSale
        options {
          values
          name
        }
        priceRange {
          minVariantPrice {
            amount
          }
          maxVariantPrice {
            amount
          }
        }
        images {
          originalSrc
          id
        }
        variants {
          priceV2 {
            amount
            currencyCode
          }
        }
      }
    }
  }
`;

export default CollectionPageTemplate;
